import React, { RefObject, useCallback, useState } from 'react';

import { DataGrid, DataGridProps, GridColumnVisibilityModel } from '@mui/x-data-grid';
import { GridColDef } from '@mui/x-data-grid/models';
import classNames from 'classnames';

import {
  Pagination,
  PaginationController,
  PaginationProps,
  paginationSpaceTopStyles,
} from '@/blocks/components/Controls/Pagination';
import { DEFAULT_DATAGRID_PAGE_SIZE, WithSkeleton } from '@/blocks/components/DataGrid/index';
import {
  DataGridRowDecoration,
  useDatagridRowDecorationsPosition,
} from '@/blocks/components/DataGrid/shared/RowDecorations';
import { ArrowDown, ArrowUp, IconArrowSwitch } from '@/blocks/components/Icons/Arrow';
import { ClassName } from '@/blocks/components/types';
import { HEADER_MAX_HEIGHT } from '@/blocks/sections/Header/Components/HeaderInner';
import { useScrollTo } from '@/hooks/useScrollTo/';

import decorationStyles from './shared/RowDecorations/styles.module.scss';
import styles from './styles.module.scss';

function checkColVisibilityModel(
  visibilityModel?: GridColumnVisibilityModel,
  columns?: readonly GridColDef[],
) {
  if (!visibilityModel || !columns) {
    return;
  }
  const visModelCols = Object.keys(visibilityModel);
  const cols = columns.map((i) => i.field);

  visModelCols.forEach((col) => {
    if (!cols.includes(col)) {
      throw new Error(
        `Invalid columnVisibilityModel provided \n\n Not found column "${col}" in ${cols.join(', ')}`,
      );
    }
  });
}

export function DataGridWrapper(
  props: DataGridProps & {
    loading: boolean;
    wrapperClassName?: ClassName;
    skeletonClassName?: ClassName;
    paginationController?: PaginationController;
    elementToScrollOnPaginate?: RefObject<HTMLElement>;
  },
) {
  const { elementToScrollOnPaginate: customElementToScrollOnPaginate } = props;
  const { scrollRef, scrollToElement } = useScrollTo();
  const { onMouseLeaveOrEnter, refDecorationRight, refDecorationLeft } = useDatagridRowDecorationsPosition();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_DATAGRID_PAGE_SIZE,
  });

  const onPaginationChange = useCallback<PaginationProps['onChange']>(
    (page) => {
      const rect = scrollRef.current?.getBoundingClientRect();
      const elementFullyVisible = rect && rect.top >= HEADER_MAX_HEIGHT;
      setPaginationModel({
        ...paginationModel,
        page: page - 1,
      });
      if (!elementFullyVisible) {
        setTimeout(() => scrollToElement(customElementToScrollOnPaginate), 0);
      }
    },
    [paginationModel, customElementToScrollOnPaginate, scrollRef, scrollToElement],
  );

  checkColVisibilityModel(props.columnVisibilityModel, props.columns);

  return (
    <div ref={scrollRef} className={classNames(props.wrapperClassName)}>
      <WithSkeleton
        loading={props.loading}
        className={props.skeletonClassName}
        rowsCount={DEFAULT_DATAGRID_PAGE_SIZE}
      >
        <DataGridRowDecoration className={decorationStyles.badgeLeft} ref={refDecorationLeft} />
        <DataGridRowDecoration className={decorationStyles.badgeRight} ref={refDecorationRight} />
        <DataGrid
          disableColumnMenu={true}
          disableRowSelectionOnClick={true}
          hideFooter={true}
          disableColumnFilter={true}
          sortingOrder={['desc', 'asc', null]}
          slots={{
            columnSortedAscendingIcon: () => <ArrowUp />,
            columnSortedDescendingIcon: () => <ArrowDown />,
            columnUnsortedIcon: () => <IconArrowSwitch />,
            noRowsOverlay: () => null,
            noResultsOverlay: () => null,
          }}
          columnHeaderHeight={76}
          rowHeight={76}
          {...props}
          slotProps={{
            baseTooltip: {
              classes: { popper: styles.dataGridColumnTooltip },
            },
            row: {
              onMouseEnter: onMouseLeaveOrEnter,
              onMouseLeave: onMouseLeaveOrEnter,
            },
          }}
          paginationModel={paginationModel}
          className={classNames(styles.datagrid, props.className)}
        />
      </WithSkeleton>
      <Pagination
        className={paginationSpaceTopStyles}
        count={props?.rows?.length || 0}
        perPage={DEFAULT_DATAGRID_PAGE_SIZE}
        onChange={onPaginationChange}
        controller={props.paginationController}
      />
    </div>
  );
}
